import { useMemo } from 'react';

// ─── Shared validation-status logic for variant/version tabs ────────────────

export type VariantValidationStatus = 'none' | 'valid' | 'invalid';

interface Asset {
  variant_number?: number | null;
  validation_result?: { is_valid?: boolean } | null;
}

/**
 * Compute per-variant validation status from a flat asset list.
 * Works for both social (CampaignAsset) and search (SearchAsset).
 */
export function useVariantValidationStatus(
  assets: Asset[] | undefined,
  variantNumbers: number[],
  defaultVariantNumber = 1,
): Record<number, VariantValidationStatus> {
  return useMemo(() => {
    const status: Record<number, VariantValidationStatus> = {};
    for (const vn of variantNumbers) {
      const va = (assets ?? []).filter((a) => (a.variant_number ?? defaultVariantNumber) === vn);
      if (va.length === 0 || !va.some((a) => a.validation_result != null)) {
        status[vn] = 'none';
      } else if (va.some((a) => a.validation_result && a.validation_result.is_valid === false)) {
        status[vn] = 'invalid';
      } else {
        status[vn] = 'valid';
      }
    }
    return status;
  }, [assets, variantNumbers, defaultVariantNumber]);
}

// ─── Icon component ─────────────────────────────────────────────────────────

export function ValidationStatusIcon({
  status,
  className = 'w-3 h-3 ml-1 inline-block',
}: {
  status: VariantValidationStatus;
  className?: string;
}) {
  if (status === 'valid') {
    return (
      <svg
        className={`${className} text-green-500`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        role="img"
        aria-label="Validation passed"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.5}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    );
  }
  if (status === 'invalid') {
    return (
      <svg
        className={`${className} text-red-500`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        role="img"
        aria-label="Validation failed"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2.5}
          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    );
  }
  return null;
}
